import React, { useState, useEffect, useRef } from 'react';
import { Mic, Square } from 'lucide-react';

const notes = ['Do', 'Do#', 'Re', 'Re#', 'Mi', 'Fa', 'Fa#', 'Sol', 'Sol#', 'La', 'La#', 'Si'];
const A4 = 440; // frequency of A4 note

function getNote(frequency: number): string {
  if (frequency === 0) return '-';
  
  // Calculate number of half steps away from A4
  const halfSteps = 12 * Math.log2(frequency / A4);
  
  // Calculate the octave
  const octave = Math.floor((halfSteps + 57) / 12); // 57 semitones from A0 to A4
  
  // Calculate the note index
  const noteIndex = Math.round(halfSteps) % 12;
  const actualNote = (noteIndex + 9) % 12; // Adjust to start from Do
  
  if (octave >= 1 && octave <= 10) {
    return `${notes[actualNote]} ${octave}`;
  }
  return '-';
}

function autoCorrelate(buffer: Float32Array, sampleRate: number): { frequency: number; clarity: number } {
  const SIZE = buffer.length;
  const MAX_SAMPLES = Math.floor(SIZE / 2);
  let bestOffset = -1;
  let bestCorrelation = 0;
  let rms = 0;
  let foundGoodCorrelation = false;

  // Calculate the RMS value
  for (let i = 0; i < SIZE; i++) {
    const val = buffer[i];
    rms += val * val;
  }
  rms = Math.sqrt(rms / SIZE);

  // Not enough signal
  if (rms < 0.01) {
    return { frequency: 0, clarity: 0 };
  }

  let lastCorrelation = 1;

  for (let offset = 0; offset < MAX_SAMPLES; offset++) {
    let correlation = 0;
    
    for (let i = 0; i < MAX_SAMPLES; i++) {
      correlation += Math.abs((buffer[i]) - (buffer[i + offset]));
    }

    correlation = 1 - (correlation / MAX_SAMPLES);
    
    if ((correlation > 0.9) && (correlation > lastCorrelation)) {
      foundGoodCorrelation = true;
      if (correlation > bestCorrelation) {
        bestCorrelation = correlation;
        bestOffset = offset;
      }
    } else if (foundGoodCorrelation) {
      let shift = (correlationShift(buffer, SIZE, bestOffset) - bestOffset) / 8;
      return {
        frequency: sampleRate / (bestOffset + shift),
        clarity: bestCorrelation
      };
    }
    lastCorrelation = correlation;
  }

  if (bestCorrelation > 0.01) {
    return {
      frequency: sampleRate / bestOffset,
      clarity: bestCorrelation
    };
  }
  
  return { frequency: 0, clarity: 0 };
}

function correlationShift(buffer: Float32Array, SIZE: number, bestOffset: number): number {
  let shift = 0;
  let correlation = 0;
  let max = 0;
  
  for (let i = 0; i < SIZE - bestOffset; i++) {
    correlation += Math.abs(buffer[i] - buffer[i + bestOffset]);
  }
  max = correlation;
  
  for (let i = 1; i < 9; i++) {
    correlation = 0;
    const offset = bestOffset + i;
    for (let j = 0; j < SIZE - offset; j++) {
      correlation += Math.abs(buffer[j] - buffer[j + offset]);
    }
    if (correlation < max) {
      max = correlation;
      shift = i;
    }
  }
  
  return bestOffset + shift;
}

function App() {
  const [isListening, setIsListening] = useState(false);
  const [pitch, setPitch] = useState<number>(0);
  const [note, setNote] = useState<string>('-');
  const [clarity, setClarity] = useState<number>(0);
  const audioContext = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const mediaStream = useRef<MediaStream | null>(null);

  const startListening = async () => {
    try {
      mediaStream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContext.current = new AudioContext();
      analyser.current = audioContext.current.createAnalyser();
      analyser.current.fftSize = 2048;
      const source = audioContext.current.createMediaStreamSource(mediaStream.current);
      source.connect(analyser.current);
      setIsListening(true);
      updatePitch();
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopListening = () => {
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach(track => track.stop());
    }
    if (audioContext.current) {
      audioContext.current.close();
    }
    setIsListening(false);
    setPitch(0);
    setNote('-');
    setClarity(0);
  };

  const updatePitch = () => {
    if (!analyser.current || !isListening || !audioContext.current) return;

    const buffer = new Float32Array(analyser.current.fftSize);
    analyser.current.getFloatTimeDomainData(buffer);
    
    const { frequency, clarity } = autoCorrelate(buffer, audioContext.current.sampleRate);
    
    if (clarity > 0.8 && frequency > 0) {
      setPitch(frequency);
      setNote(getNote(frequency));
      setClarity(clarity);
    }

    requestAnimationFrame(updatePitch);
  };

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 w-full max-w-md shadow-xl border border-white/20">
        <h1 className="text-3xl font-bold text-center text-white mb-8">
          Afinador de Voz Profesional
        </h1>

        <div className="space-y-8">
          <div className="text-center">
            <div className="text-7xl font-bold text-white mb-3">{note}</div>
            <div className="text-2xl text-white/80">
              {pitch.toFixed(1)} Hz
            </div>
            <div className="w-full bg-white/20 rounded-full h-3 mt-6">
              <div
                className="bg-emerald-400 h-3 rounded-full transition-all duration-300"
                style={{ width: `${clarity * 100}%` }}
              />
            </div>
          </div>

          <button
            onClick={isListening ? stopListening : startListening}
            className={`w-full py-4 px-6 rounded-xl flex items-center justify-center gap-3 text-white font-semibold transition-all duration-300 ${
              isListening
                ? 'bg-red-500 hover:bg-red-600'
                : 'bg-emerald-500 hover:bg-emerald-600'
            }`}
          >
            {isListening ? (
              <>
                <Square className="w-6 h-6" />
                Detener
              </>
            ) : (
              <>
                <Mic className="w-6 h-6" />
                Iniciar
              </>
            )}
          </button>
        </div>

        <div className="mt-6 text-center text-white/60 text-sm">
          Rango: Do1 - Do10
        </div>
      </div>
    </div>
  );
}

export default App;